import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  Grid,
  Spacer,
  Text,
  useMediaQuery,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SearchButton } from './SearchApartmentButton/SearchButton';
import { GuestsSelector } from './GuestsSelector/GuestsSelector';
import { BedroomsSelector } from './BedroomsSelector/BedroomsSelector';
import {
  CHAKRA_SELECT_TYPE,
  ChakraSelect,
  ChakraSelectChildrenCategory,
  ChakraSelectParentsCategory,
  ChakraSelectSubChildrenCategory,
} from '../../common/ChakraSelect/ChakraSelect';
import { FeatureList } from './FeatureList/FeatureList';
import { RegionList } from './RegionList/RegionList';
import {
  getChakraSelectCitiesData,
  getChakraSelectCountriesData,
  getChakraSelectLocationSelected,
  getChakraSelectRegionsData,
  getUpdatedObjectFilterLocationParams,
} from '../../../services/Objects/ObjectsService';
import { StringOrNumber } from '@chakra-ui/utils';
import { DateRangeSelector } from './DateRangeSelector/DateRangeSelector';
import { ObjectsParams } from '../../../types/ParamsTypes';
import { ObjectsResult, ObjectsSelectionFeature } from '../../../types/ObjectsTypes';
import { APP_VIEW, getAppView } from '../../../services/VersionManagement/VersionManagementService';

export interface ObjectFilterProps {
  data: ObjectsResult;
  handleFilterChange: (updatedObjectsParams: ObjectsParams) => void;
  handleFilterReset: () => void;
  objectsParams: ObjectsParams;
  selectedObjectTypeFeatures: StringOrNumber[];
  selectedCommonFeatures: StringOrNumber[];
  handleObjectTypeFeatureChange: (values: StringOrNumber[]) => void;
  handleCommonFeatureChange: (values: StringOrNumber[]) => void;
  handleLoadingSpinner: (value: boolean) => void;
  showResults: boolean;
  setShowResults: (value: boolean) => void;
}

export const Filter = (props: ObjectFilterProps): JSX.Element => {
  const {
    data,
    handleFilterChange,
    objectsParams,
    handleFilterReset,
    handleObjectTypeFeatureChange,
    handleCommonFeatureChange,
    selectedObjectTypeFeatures,
    selectedCommonFeatures,
    handleLoadingSpinner,
    setShowResults,
    showResults,
  } = props;
  const { selection } = data;
  const { features, locations } = selection;
  const { countries, cities, regions } = locations;
  const [locationCountries, setLocationCountries] = useState<ChakraSelectParentsCategory>();
  const [locationRegions, setLocationRegions] = useState<ChakraSelectChildrenCategory>();
  const [locationCities, setLocationCities] = useState<ChakraSelectSubChildrenCategory>();
  const [objectTypeFeatures, setObjectTypeFeatures] = useState<Array<ObjectsSelectionFeature>>([]);
  const [commonFeatures, setCommonFeatures] = useState<Array<ObjectsSelectionFeature>>([]);
  const [appView] = useState<APP_VIEW>(getAppView());
  const [isTabletOrMobile] = useMediaQuery('(max-width: 767px)');

  const locationSelected = useMemo(
    () => getChakraSelectLocationSelected(objectsParams, locations),
    [objectsParams, locations],
  );

  useEffect(() => {
    setLocationCountries(getChakraSelectCountriesData(countries));
  }, [countries]);

  useEffect(() => {
    setLocationRegions(getChakraSelectRegionsData(regions));
  }, [regions]);

  useEffect(() => {
    setLocationCities(getChakraSelectCitiesData(cities));
  }, [cities]);

  useEffect(() => {
    const filteredObjectTypeFeatures = features.filter(feature => feature.MarkType === 'M10');
    const filteredCommonFeatures = features.filter(feature => feature.MarkType !== 'M10');
    setObjectTypeFeatures(filteredObjectTypeFeatures);
    setCommonFeatures(filteredCommonFeatures);
  }, [features]);

  const handleLocationChange = useCallback(
    (type: CHAKRA_SELECT_TYPE, id) => {
      handleFilterChange({
        ...objectsParams,
        ...getUpdatedObjectFilterLocationParams(type, id),
      });
    },
    [objectsParams, handleFilterChange],
  );

  const dateRangeSelectorMobileContext = useMemo(
    () => (isTabletOrMobile ? 'FILTER_MOBILE' : ''),
    [isTabletOrMobile],
  );

  return (
    <Box bg={'white'} p={4} borderRadius={4} w={'100%'} mt={{ sm: 16, md: 0, lg: 0, xl: 0 }}>
      {!isTabletOrMobile && showResults && (
        <>
          <Text textStyle={'category.primary'}>Filter</Text>
          <Divider mt={1} mb={4} />
        </>
      )}
      <Grid
        gap={4}
        display={showResults ? 'grid' : 'flex'}
        flexDirection={{ sm: 'column', md: 'column', lg: 'row', xl: 'row' }}
        direction="column">
        {appView === APP_VIEW.ALL && (
          <FormControl>
            <ChakraSelect
              headline={'Wo soll es hingehen?'}
              emptyStateText={'Beliebig'}
              parents={locationCountries}
              children={locationRegions}
              subChildren={locationCities}
              onChange={handleLocationChange}
              selected={locationSelected}
              closeOnSelect={true}
            />
          </FormControl>
        )}
        {appView === APP_VIEW.MIN && (
          <div style={{ display: "inline-flex", gap: "12px", flexDirection: isTabletOrMobile ? "column" : "row" }} >
            <FormControl minW={"fit-content"} >
              <DateRangeSelector
                onFilterChange={handleFilterChange}
                objectsParams={objectsParams}
                context={dateRangeSelectorMobileContext}
              />
            </FormControl>

            <FormControl minW={"fit-content"}>
              <GuestsSelector objectsParams={objectsParams} onFilterChange={handleFilterChange} />
            </FormControl>
          </div>
        )}
        {appView !== APP_VIEW.MIN && (
          <>
            <FormControl >
              <DateRangeSelector
                onFilterChange={handleFilterChange}
                objectsParams={objectsParams}
                context={dateRangeSelectorMobileContext}
              />
            </FormControl>

            <FormControl >
              <GuestsSelector objectsParams={objectsParams} onFilterChange={handleFilterChange} />
            </FormControl>
          </>
        )}

        {appView === APP_VIEW.ALL && (
          <FormControl>
            <BedroomsSelector onFilterChange={handleFilterChange} objectsParams={objectsParams} />
          </FormControl>
        )}
        {showResults && (
          <Flex>
            <Spacer />
            <Button size={'xs'} variant="secondary" onClick={handleFilterReset}>
              Filter löschen
            </Button>
          </Flex>
        )}
        {!showResults && (
          <Flex>
            <Spacer />
            <Button
              size={'xl'}
              w={{ sm: '100%', md: '100%', lg: 120, xl: 120 }}
              h={14}
              variant="primary"
              onClick={() => setShowResults(true)}>
              Suchen
            </Button>
          </Flex>
        )}

        {appView === APP_VIEW.ALL && (
          <FeatureList
            selectedItems={selectedObjectTypeFeatures}
            onFilterChange={handleObjectTypeFeatureChange}
            features={objectTypeFeatures}
            header={'Objektart'}
          />
        )}
        {appView === APP_VIEW.ALL && (
          <RegionList
            objectsParams={objectsParams}
            regions={regions}
            onFilterChange={handleFilterChange}
          />
        )}

        {appView === APP_VIEW.ALL && (
          <FeatureList
            selectedItems={selectedCommonFeatures}
            features={commonFeatures}
            onFilterChange={handleCommonFeatureChange}
            header={'Ausstattung'}
          />
        )}
      </Grid>
    </Box>
  );
};
